import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '@design-system';
import { useTimedSequence } from '../hooks/use-timed-sequence';
import { CONSUMER_STEPS, type ConsumerStepId } from '../lib/steps';

interface LoadingSequenceProps {
  step: ConsumerStepId;
  title: string;
  subtitle: string;
  lines: string[];
  isReady: boolean;
  onDone: () => void;
  totalMs?: number;
}

const chipSpring = { type: 'spring', stiffness: 500, damping: 30, mass: 1 } as const;

/**
 * The quote-search animation guided planning uses: each line springs in as a chip and is ticked
 * once done, over a filling progress bar.
 */
export function LoadingSequence({
  step,
  title,
  subtitle,
  lines,
  isReady,
  onDone,
  totalMs = 4800,
}: LoadingSequenceProps) {
  const { completed, progress } = useTimedSequence(lines.length, totalMs, isReady, onDone);
  const shownLines = lines.slice(0, Math.min(completed + 1, lines.length));

  return (
    <MotionConfig reducedMotion="user">
      <section
        className="mx-auto flex w-full max-w-4xl flex-col items-center gap-8 py-6 text-center"
        aria-busy="true"
        data-testid="consumer-loading"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          className="flex flex-col items-center gap-2"
        >
          <p className="text-primary text-xs font-semibold uppercase tracking-wide">{CONSUMER_STEPS[step].kicker}</p>
          <h1 className="text-2xl font-semibold text-gray-950 sm:text-3xl">{title}</h1>
          <p className="text-muted-foreground max-w-2xl">{subtitle}</p>
        </motion.div>

        <ol className="flex flex-wrap justify-center gap-3">
          <AnimatePresence initial={false}>
            {shownLines.map((line, index) => {
              const isDone = index < completed;
              return (
                <motion.li
                  key={line}
                  layout
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1, transition: chipSpring }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium',
                    isDone ? 'bg-primary/10 text-primary' : 'bg-primary/5 text-primary/80',
                  )}
                >
                  <AnimatePresence mode="wait">
                    {isDone && (
                      <motion.span
                        key="check"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1, transition: { ...chipSpring, damping: 25 } }}
                        className="inline-flex"
                      >
                        <Check className="size-4" aria-hidden />
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {line}
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ol>

        <motion.div
          initial={{ opacity: 0, scaleX: 0.9 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
          className="w-full max-w-2xl"
        >
          <div
            role="progressbar"
            aria-label={title}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200"
          >
            <motion.div
              className="bg-primary h-full rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </motion.div>
      </section>
    </MotionConfig>
  );
}
